/**
 * The app shell: a sidebar on a laptop, a top bar and a tab bar on a phone.
 *
 * The same destinations in the same order either way, so muscle memory carries
 * between the web and the native apps. Five is the ceiling on the tab bar — a sixth
 * makes all six harder to hit.
 *
 * `footer` — the account link and sign-out — used to render only inside the
 * sidebar, which is hidden below 901px. That stranded the entire profile branch
 * and signing out on a phone: those pages rendered fine, but nothing anywhere
 * linked to them. It now renders in both chromes, the same way the destinations
 * do, and the same way round: one is in the DOM per breakpoint, chosen by a
 * media query rather than by a resize listener.
 *
 * Navigation is left to the caller: this package does not depend on a router.
 */

import type { ReactNode } from 'react'
import { GlassBar } from './Surface'

export interface NavDestination {
  to: string
  label: string
  icon: ReactNode
  /** Shown on the tab bar. Destinations past the fifth are sidebar-only. */
  onTabBar?: boolean
  badge?: number
}

export interface ShellProps {
  destinations: NavDestination[]
  /** Renders one destination as a link, and marks the current one with
   *  `aria-current`. Supplied by the app so the router stays out of this package. */
  renderLink: (
    destination: NavDestination,
    props: { className: string; children: ReactNode },
  ) => ReactNode
  footer?: ReactNode
  banner?: ReactNode
  children: ReactNode
}

export function AppShell({ destinations, renderLink, footer, banner, children }: ShellProps) {
  const tabs = destinations.filter((d) => d.onTabBar !== false).slice(0, 5)

  return (
    <div className="mx-shell">
      <a className="mx-skip-link" href="#main-content">
        Skip to content
      </a>

      {footer && (
        <div className="mx-topbar mx-topbar-mobile">
          <span className="mx-brand-mark" aria-hidden="true" />
          <div className="mx-topbar-actions">{footer}</div>
        </div>
      )}

      <GlassBar className="mx-sidebar mx-sidebar-desktop">
        <div className="mx-brand">
          <span className="mx-brand-mark" aria-hidden="true" />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>Modulus</span>
        </div>

        <nav className="mx-nav" aria-label="Main">
          {destinations.map((destination) =>
            renderLink(destination, {
              className: 'mx-nav-item',
              children: (
                <>
                  <span aria-hidden="true" style={{ display: 'grid', placeItems: 'center' }}>
                    {destination.icon}
                  </span>
                  <span>{destination.label}</span>
                  {destination.badge ? (
                    <span className="mx-nav-badge">{destination.badge}</span>
                  ) : null}
                </>
              ),
            }),
          )}
        </nav>

        {footer && <div style={{ marginTop: 'auto' }}>{footer}</div>}
      </GlassBar>

      <main className="mx-main" id="main-content" tabIndex={-1}>
        {banner && <div style={{ marginBottom: 'var(--space-m)' }}>{banner}</div>}
        {children}
      </main>

      {/* Fades content as it approaches the tab bar, so the bar reads as
          floating above a page rather than clipping it. */}
      <div className="mx-scroll-edge mx-tabbar-mobile" aria-hidden="true" />

      <GlassBar className="mx-tabbar mx-tabbar-mobile">
        <nav
          style={{ display: 'flex', width: '100%' }}
          aria-label="Main"
        >
          {tabs.map((destination) =>
            renderLink(destination, {
              className: 'mx-tab',
              children: (
                <>
                  <span aria-hidden="true">{destination.icon}</span>
                  <span>{destination.label}</span>
                </>
              ),
            }),
          )}
        </nav>
      </GlassBar>
    </div>
  )
}
